/**
 * Widget registry — maps widget_id strings to React components.
 *
 * To add a new widget:
 *   1. Build a component file like widgets/treasury/UnreconciledAlert.tsx
 *      that takes { spec, workspaceId } props
 *   2. Register the widget_id below
 *   3. AI calls `deploy_widget({widget_id, title, params, layout: {x,y,w,h}})`
 *      and the widget appears on the dashboard immediately
 *
 * The framework is generic — widgets are just React components that
 * fetch their own data from /api/v1 endpoints (or via the MCP tool
 * surface) and render. Each is independently authored, ~50-100 lines.
 */
import { TreasuryUnreconciledAlert } from "./treasury/UnreconciledAlert";
import { ChampionsRoll } from "./designations/ChampionsRoll";
import { TargetsRoll } from "./designations/TargetsRoll";
import { TopAttention } from "./homework/TopAttention";
import { FieldTierBreakdown } from "./field/TierBreakdown";
import { FieldNeverEngaged } from "./field/NeverEngaged";
import { FieldRecentTouches } from "./field/RecentTouches";
import { BillsTrackedRecent } from "./bills/TrackedRecent";
import { BillsStalled } from "./bills/Stalled";
import { WatchedAmendments } from "./bills/WatchedAmendments";
import { ProvisionsTracker } from "./bills/ProvisionsTracker";
import { BillsWhipMath } from "./bills/WhipMath";
import { IssuesPortfolioOverview } from "./issues/PortfolioOverview";
import { CoalitionRecentFeed } from "./coalition/RecentFeed";
import { MembersCoolingAlert } from "./members/CoolingAlert";
import { MembersRelationshipStrength } from "./members/RelationshipStrength";
import {
  DashboardNarration,
  DashboardWorkspaceSummary,
  DashboardTopAttention,
  DashboardPolicyIntelligence,
  DashboardTrackedBills,
  DashboardTopRelationships,
  DashboardFeedWidget,
  DashboardActivityStreamWidget,
  DashboardWidgetGridLegacy,
  DashboardScorecards,
} from "./dashboard/dashboard-widgets";

export interface WidgetSpec {
  widget_id: string;             // e.g. "treasury.unreconciled_alert"
  instance_id?: string;          // optional discriminator if multiple instances of same widget_id
  title?: string;                // user-visible title (PanelRenderer may omit it)
  params?: Record<string, unknown>;  // widget-specific parameters
  layout?: { x: number; y: number; w: number; h: number };
}

export interface WidgetProps {
  spec: WidgetSpec;
  workspaceId: string;
}

export const WIDGET_REGISTRY: Record<string, React.FC<WidgetProps>> = {
  // Treasury
  "treasury.unreconciled_alert": TreasuryUnreconciledAlert,
  // Designations / champions
  "designations.champions_roll": ChampionsRoll,
  "designations.targets_roll": TargetsRoll,
  // Coalition feed
  "coalition.recent_feed": CoalitionRecentFeed,
  // Homework / attention queue
  "homework.top_attention": TopAttention,
  // Field & grassroots (GAP-1 / FB-R7Y2LK5Z)
  "field.tier_breakdown": FieldTierBreakdown,
  "field.never_engaged": FieldNeverEngaged,
  "field.recent_touches": FieldRecentTouches,
  // Bills
  "bills.tracked_recent": BillsTrackedRecent,
  "bills.stalled": BillsStalled,
  "bills.watched_amendments": WatchedAmendments,
  "bills.provisions_tracker": ProvisionsTracker,
  // Issues
  "issues.portfolio_overview": IssuesPortfolioOverview,
  // Members & relationships (FB-J3V15AT5, FB-04LY0RA5)
  "members.cooling_alert": MembersCoolingAlert,
  "members.relationship_strength": MembersRelationshipStrength,
  // Bills — whip math (FB-HOJP2J62)
  "bills.whip_math": BillsWhipMath,
  // Dashboard sections — exposed as widget kinds so the AI can compose
  // a complete dashboard layout via compose_panel(page_kind:
  // "dashboard"). When the AI composes a panel for the dashboard, the
  // default chrome is replaced; reach for these widget_ids to keep any
  // built-in section.
  "dashboard.narration": DashboardNarration,
  "dashboard.workspace_summary": DashboardWorkspaceSummary,
  "dashboard.top_attention": DashboardTopAttention,
  "dashboard.policy_intelligence": DashboardPolicyIntelligence,
  "dashboard.tracked_bills": DashboardTrackedBills,
  "dashboard.top_relationships": DashboardTopRelationships,
  "dashboard.scorecards": DashboardScorecards,
  "dashboard.feed": DashboardFeedWidget,
  "dashboard.activity_stream": DashboardActivityStreamWidget,
  "dashboard.widget_grid": DashboardWidgetGridLegacy,
};
